import React, { useState, useCallback } from 'react';
import { 
  Box, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  TextField, 
  Typography,
  Paper,
  Divider,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SpinnerButton from '../../components/common/SpinnerButton';
import classes from './drugex.module.css';
import { apiConfig } from '../../api/ApiConfig';
import { useSelector } from 'react-redux';

// Constants moved outside component to prevent recreation on each render
const PREDICTION_STATES = {
  INIT: 'INIT',
  LOADING: 'LOADING',
  COMPLETE: 'COMPLETE',
  ERROR: 'ERROR',
};

const INPUT_TYPE_OPTIONS = ['Name', 'PubChem CID', 'SMILES'];

// Fallback to inline styles if styled components cause issues
const getHeaderStyles = (theme) => ({
  background: 'linear-gradient(135deg, #8B5A87 0%, #6B4968 100%)',
  color: '#FFFFFF',
  padding: '24px 32px',
  borderRadius: '16px',
  marginBottom: '24px',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
});

const getContentStyles = () => ({
  background: '#FFFFFF',
  padding: '24px',
  borderRadius: '16px',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
  border: '1px solid #E5E7EB',
});

const getInputSectionStyles = () => ({
  background: '#F9FAFB',
  padding: '24px',
  borderRadius: '12px',
  border: '1px solid #E5E7EB',
  marginBottom: '24px',
});

// Styled components for professional appearance matching ADMET design
const HeaderContainer = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, #8B5A87 0%, #6B4968 100%)',
  color: '#FFFFFF',
  padding: theme.spacing(3, 4),
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
}));

const ContentContainer = styled(Paper)(({ theme }) => ({
  background: '#FFFFFF',
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
  border: '1px solid #E5E7EB',
}));

const InputSection = styled(Box)(({ theme }) => ({
  background: '#F9FAFB',
  padding: theme.spacing(3),
  borderRadius: theme.spacing(1.5),
  border: '1px solid #E5E7EB',
  marginBottom: theme.spacing(3),
}));

const ResultContainer = styled(Paper)(({ theme }) => ({
  background: '#FAFAFA',
  padding: theme.spacing(3),
  borderRadius: theme.spacing(1.5),
  border: '1px solid #E0E0E0',
  marginTop: theme.spacing(2),
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  minWidth: 200,
  [theme.breakpoints.down('sm')]: {
    minWidth: '100%',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  minWidth: 300,
  [theme.breakpoints.down('sm')]: {
    minWidth: '100%',
  },
}));

export const Drugex = () => {
  const [apiInput, setApiInput] = useState('');
  const [apiOption, setApiOption] = useState('Name');
  const [excipientInput, setExcipientInput] = useState('');
  const [excipientOption, setExcipientOption] = useState('Name');
  const [result, setResult] = useState(null);
  const [predictionState, setPredictionState] = useState(PREDICTION_STATES.INIT);
  const [errorMessage, setErrorMessage] = useState('');
  const { userId, isTokenAuth } = useSelector((state) => state.auth);

  const { fetchDrugex, logUserEvent } = apiConfig();

  // Memoized prediction function
  const handlePredict = useCallback(async () => {
    if (!apiInput?.trim() || !excipientInput?.trim()) {
      setErrorMessage('Both API and Excipient inputs are required');
      return;
    }

    setPredictionState(PREDICTION_STATES.LOADING);
    setErrorMessage('');

    if (userId) {
        logUserEvent({
            userId,
            eventType: 'ACTION',
            eventName: 'PREDICT_DRUG_EXCIPIENT_COMPATIBILITY',
            metadata: {
                apiInput: apiInput.trim(),
                apiOption,
                excipientInput: excipientInput.trim(),
                excipientOption,
                token_auth: isTokenAuth || false
            },
            timestamp: new Date().toISOString()
        });
    }
    
    try {
      const requestData = {
        api_input: apiInput.trim(),
        api_option: apiOption,
        excipient_input: excipientInput.trim(),
        excipient_option: excipientOption
      };
      
      const data = await fetchDrugex(requestData);
      
      if (data) {
        setResult(data);
        setPredictionState(PREDICTION_STATES.COMPLETE);
      } else {
        throw new Error('No data received from server');
      }
    } catch (error) {
      console.error('Prediction failed:', error);
      setErrorMessage(error.message || 'Prediction failed. Please try again.');
      setPredictionState(PREDICTION_STATES.ERROR);
      setResult(null);
    }
  }, [apiInput, apiOption, excipientInput, excipientOption, fetchDrugex]);

  // Clear all inputs and results
  const handleClear = useCallback(() => {
    setApiInput('');
    setApiOption('Name');
    setExcipientInput('');
    setExcipientOption('Name');
    setResult(null);
    setPredictionState(PREDICTION_STATES.INIT);
    setErrorMessage('');
  }, []);

  // Handle input changes with state reset
  const handleApiInputChange = useCallback((e) => {
    setApiInput(e.target.value);
    if (predictionState !== PREDICTION_STATES.INIT) {
      setPredictionState(PREDICTION_STATES.INIT);
    }
    setErrorMessage('');
  }, [predictionState]);

  const handleExcipientInputChange = useCallback((e) => {
    setExcipientInput(e.target.value);
    if (predictionState !== PREDICTION_STATES.INIT) {
      setPredictionState(PREDICTION_STATES.INIT);
    }
    setErrorMessage('');
  }, [predictionState]);

  const isLoading = predictionState === PREDICTION_STATES.LOADING;
  const hasError = predictionState === PREDICTION_STATES.ERROR;
  const hasResult = predictionState === PREDICTION_STATES.COMPLETE && result;

  return (
    <div>
      {/* Professional Header */}
      <HeaderContainer elevation={0}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 600, 
                marginBottom: 1,
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}
            >
              Drug-Excipient Compatibility
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                opacity: 0.9,
                fontSize: '1.1rem',
                lineHeight: 1.5
              }}
            >
              Predict compatibility between active pharmaceutical ingredients and excipients
            </Typography>
          </Box>
          <Box sx={{ 
            display: { xs: 'none', md: 'block' },
            opacity: 0.3,
            fontSize: '4rem',
            fontWeight: 300
          }}>
            💊
          </Box>
        </Box>
      </HeaderContainer>

      {/* Main Content */}
      <ContentContainer elevation={0}>
        {/* Input Section */}
        <InputSection>
          <Typography 
            variant="h6" 
            sx={{ 
              marginBottom: 2, 
              color: '#374151',
              fontWeight: 600
            }}
          >
            Compound Information
          </Typography>
          <Divider sx={{ marginBottom: 3, backgroundColor: '#E5E7EB' }} />
          
          {/* API Input Row */}
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 2, 
            marginBottom: 3,
            alignItems: 'flex-start'
          }}>
            <StyledFormControl>
              <InputLabel id="apiOptionLabel">API Input Type</InputLabel>
              <Select
                labelId="apiOptionLabel"
                value={apiOption}
                label="API Input Type"
                onChange={(e) => setApiOption(e.target.value)}
                disabled={isLoading}
              >
                {INPUT_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </StyledFormControl>

            <StyledTextField
              label="Active Pharmaceutical Ingredient (API)"
              value={apiInput}
              onChange={handleApiInputChange}
              disabled={isLoading}
              placeholder={`Enter ${apiOption.toLowerCase()}...`}
              helperText={`Specify the API using ${apiOption}`}
            />
          </Box>

          {/* Excipient Input Row */}
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 2, 
            alignItems: 'flex-start'
          }}>
            <StyledFormControl>
              <InputLabel id="excipientOptionLabel">Excipient Input Type</InputLabel>
              <Select
                labelId="excipientOptionLabel"
                value={excipientOption}
                label="Excipient Input Type"
                onChange={(e) => setExcipientOption(e.target.value)}
                disabled={isLoading}
              >
                {INPUT_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </StyledFormControl>

            <StyledTextField
              label="Excipient"
              value={excipientInput}
              onChange={handleExcipientInputChange}
              disabled={isLoading}
              placeholder={`Enter ${excipientOption.toLowerCase()}...`}
              helperText={`Specify the excipient using ${excipientOption}`}
            />
          </Box>

          {/* Action Buttons */}
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            marginTop: 3,
            justifyContent: 'flex-start'
          }}>
            <SpinnerButton
              label="Predict Compatibility"
              onClick={handlePredict}
              loading={isLoading}
              disabled={!apiInput?.trim() || !excipientInput?.trim() || isLoading}
              sx={{
                background: 'linear-gradient(135deg, #8B5A87 0%, #6B4968 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #6B4968 0%, #5A3C57 100%)',
                },
                '&:disabled': {
                  background: '#E5E7EB',
                  color: '#9CA3AF',
                },
                minHeight: '42px',
                fontWeight: 600,
                textTransform: 'none',
                letterSpacing: '0.5px',
                paddingX: 3
              }}
            />
            <SpinnerButton
              label="Clear All"
              buttonColor="#F3F4F6"
              textColor="#374151"
              onClick={handleClear}
              disabled={isLoading}
              sx={{
                border: '1px solid #D1D5DB',
                '&:hover': {
                  backgroundColor: '#E5E7EB',
                },
                minHeight: '42px',
                fontWeight: 600,
                textTransform: 'none',
                paddingX: 3
              }}
            />
          </Box>
        </InputSection>

        {/* Error Display */}
        {hasError && errorMessage && (
          <Alert 
            severity="error" 
            sx={{ marginBottom: 3 }}
            onClose={() => {
              setErrorMessage('');
              setPredictionState(PREDICTION_STATES.INIT);
            }}
          >
            {errorMessage}
          </Alert>
        )}

        {/* Results Section */}
        <Box>
          <Typography 
            variant="h6" 
            sx={{ 
              marginBottom: 2, 
              color: '#374151',
              fontWeight: 600
            }}
          >
            Compatibility Analysis Results
          </Typography>
          <Divider sx={{ marginBottom: 2, backgroundColor: '#E5E7EB' }} />
          
          {hasResult ? (
            <ResultContainer elevation={0}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Compatibility Result */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151' }}>
                    Compatibility:
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 700,
                      color: result.result?.toLowerCase() === 'compatible' ? '#059669' : '#DC2626',
                      textTransform: 'capitalize'
                    }}
                  >
                    {result.result || 'Unknown'}
                  </Typography>
                </Box>

                {/* Probability Score */}
                {result.probability !== undefined && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#374151' }}>
                      Confidence Score:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {(typeof result.probability === 'number' 
                        ? (result.probability * 100).toFixed(1) 
                        : result.probability)}%
                    </Typography>
                  </Box>
                )}

                {/* Additional Notes */}
                {result.note && (
                  <Box>
                    <Typography variant="body2" sx={{ 
                      color: '#6B7280',
                      fontStyle: 'italic',
                      marginTop: 1,
                      padding: 2,
                      backgroundColor: '#F8FAFC',
                      borderRadius: 1,
                      border: '1px solid #E2E8F0'
                    }}>
                      {result.note}
                    </Typography>
                  </Box>
                )}

                {/* Input Summary */}
                <Divider sx={{ marginY: 2 }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ 
                    fontWeight: 600, 
                    color: '#374151',
                    marginBottom: 1
                  }}>
                    Analysis Summary:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>API:</strong> {apiInput} ({apiOption})
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Excipient:</strong> {excipientInput} ({excipientOption})
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </ResultContainer>
          ) : predictionState === PREDICTION_STATES.INIT ? (
            <Box sx={{ 
              textAlign: 'center', 
              padding: 4,
              color: '#6B7280'
            }}>
              <Typography variant="body1">
                Enter compound information above and click "Predict Compatibility" to analyze drug-excipient interactions
              </Typography>
            </Box>
          ) : isLoading ? (
            <Box sx={{ 
              textAlign: 'center', 
              padding: 4,
              color: '#6B7280'
            }}>
              <Typography variant="body1">
                Analyzing compatibility...
              </Typography>
            </Box>
          ) : null}
        </Box>
      </ContentContainer>
    </div>
  );
};
